import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowUpRight, ShieldCheck, TrendingUp, Crosshair, Lightbulb, Lock, Rocket, Quote, FileCheck, Clock, PackageCheck, Globe, Star, Plus, Minus } from 'lucide-react';
import { LocalLink as Link } from '../components/LocalLink';
import { Helmet } from 'react-helmet-async';
import SeoHead from '../components/SeoHead';
import { useTranslation, Trans } from 'react-i18next';
import { projects } from '../data/projects';

const homeCategories = [
  { name: 'Ceramic Artwork', slug: 'ceramic-artwork', image: '/Products/Ceramic Artwork/wps_doc_102.webp' },
  { name: 'Fabric & Hand Weaving', slug: 'fabric-hand-weaving', image: '/Products/Fabric & Hand Weaving/wps_doc_15.webp' },
  { name: 'Leather Artwork', slug: 'leather-artwork', image: '/Products/Leather Artwork/wps_doc_1.webp' },
  { name: 'Mixed Media Artwork', slug: 'mixed-media-artwork', image: '/Products/Mixed Media Artwork/wps_doc_39.webp' },
  { name: 'Oil Painting & Acrylic on Canvas', slug: 'oil-painting-acrylic-on-canvas', image: '/Products/Oil Painting & Acrylic on Canvas/wps_doc_61.webp' },
  { name: 'Original Design', slug: 'original-design', image: '/Products/Original Design/wps_doc_70.webp' },
  { name: 'Paper Artwork', slug: 'paper-artwork', image: '/Products/Paper Artwork/wps_doc_44.webp' },
  { name: 'Wooden Artwork', slug: 'wooden-artwork', image: '/Products/Wooden Artwork/wps_doc_3.webp' },
];



const HomeFAQItem = ({ question, answer }: { question: string, answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-spring-text/10 py-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left focus:outline-none group"
      >
        <span className="text-xl md:text-2xl font-light text-spring-text group-hover:text-spring-accent3 transition-colors pr-8">
          {question}
        </span>
        <div className="flex-shrink-0 text-spring-text group-hover:text-spring-accent3 transition-colors">
          {isOpen ? <Minus size={24} strokeWidth={1} /> : <Plus size={24} strokeWidth={1} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pt-6 text-spring-text/70 font-light leading-relaxed text-lg max-w-4xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface HomeExhibitionItem {
  image: string;
  closeup: string;
  mockup: string;
  dimension: string;
  title: string;
  desc: string;
  detailUrl: string;
}

function HomeExhibitionMedia({ piece, isFrench, isArabic }: { piece: HomeExhibitionItem; isFrench: boolean; isArabic: boolean }) {
  const [isHovered, setIsHovered] = useState(false);
  const [activePanel, setActivePanel] = useState<number | null>(null);

  const panels = [
    { key: 'design', label: isFrench ? 'Design' : isArabic ? 'التصميم' : 'Design', src: piece.image },
    { key: 'detail', label: isFrench ? 'Détail' : isArabic ? 'التفاصيل' : 'Detail', src: piece.closeup },
    { key: 'mockup', label: isFrench ? 'Mise en scène' : isArabic ? 'المحاكاة' : 'Mockup', src: piece.mockup },
  ];

  return (
    <div
      className="block relative aspect-[3/4] overflow-hidden bg-spring-text/5 cursor-pointer group/media"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setActivePanel(null);
      }}
    >
      {/* Background link to detail */}
      <Link
        to={piece.detailUrl}
        className="absolute inset-0 z-0"
        aria-label={`${piece.title} — View Piece`}
      />

      {/* Default view: Pins Exhibition graphic */}
      <img
        src={piece.image}
        alt={`${piece.title} (${piece.dimension}) — 3D tactile wall art relief for luxury hotels, sculpted mineral plaster — Clouds Art`}
        className={`w-full h-full object-cover transition-all duration-500 ease-out pointer-events-none ${
          isHovered ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
        }`}
        loading="lazy"
      />

      {/* 3-panel split view on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            key="split-panels"
            className="absolute inset-0 flex pointer-events-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {panels.map((panel, idx) => {
              const isPanelActive = activePanel === idx;
              const hasActivePanel = activePanel !== null;
              const flexClass = !hasActivePanel 
                ? 'flex-1' 
                : isPanelActive 
                  ? 'flex-[1.6]' 
                  : 'flex-[0.7]';

              return (
                <motion.div
                  key={panel.key}
                  className={`relative ${flexClass} h-full overflow-hidden transition-all duration-300 ease-out`}
                  initial={{ opacity: 0, scaleX: 0.85 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  exit={{ opacity: 0, scaleX: 0.85 }}
                  transition={{ duration: 0.3, delay: idx * 0.04, ease: [0.4, 0, 0.2, 1] }}
                  style={{ transformOrigin: idx === 0 ? 'left' : idx === 2 ? 'right' : 'center' }}
                  onMouseEnter={() => setActivePanel(idx)}
                >
                  <Link to={piece.detailUrl} className="block w-full h-full">
                    <img
                      src={panel.src}
                      alt={`${panel.label} — ${piece.title}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Thin divider line between panels */}
                  {idx < 2 && (
                    <div className="absolute top-0 right-0 bottom-0 w-[1.5px] bg-white/80 z-10 pointer-events-none" />
                  )}

                  {/* Bottom luxury gradient label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-6 pb-2.5 px-1.5 flex items-end justify-center pointer-events-none">
                    <span className={`font-medium uppercase tracking-widest text-white/95 text-center transition-all duration-200 ${
                      isPanelActive ? 'text-[11px] font-semibold text-white' : 'text-[9px]'
                    }`}>
                      {panel.label}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating dual CTA buttons on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="absolute top-3 inset-x-3 flex items-center justify-between gap-2 z-20 pointer-events-auto"
          >
            <Link
              to={piece.detailUrl}
              className="flex-1 py-1.5 px-2 bg-white/95 hover:bg-white text-spring-text backdrop-blur-md shadow-sm text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-center inline-flex items-center justify-center gap-1 transition-all duration-200 hover:shadow-md"
              onClick={(e) => e.stopPropagation()}
            >
              <span>View Piece</span>
              <ArrowRight size={11} />
            </Link>
            <Link
              to="/catalogue/exhibition-artworks"
              className="flex-1 py-1.5 px-2 bg-spring-text/90 hover:bg-spring-text text-white backdrop-blur-md shadow-sm text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-center inline-flex items-center justify-center gap-1 transition-all duration-200 hover:shadow-md hover:bg-spring-accent3 hover:text-black"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Explore All</span>
              <ArrowUpRight size={11} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dimension Badge */}
      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-2.5 py-1 text-[10px] font-semibold tracking-wider text-spring-text uppercase shadow-sm z-20 pointer-events-none">
        {piece.dimension}
      </div>
    </div>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language.startsWith('fr');
  const isArabic = i18n.language.startsWith('ar');
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);

  const heroSlides = [
    {
      image: '/images/hero 2.webp',
      titleKey: 'home.hero.title',
      titleFallback: <Trans i18nKey="home.hero.title">Pending Clouds <span className="italic font-light">Art</span></Trans>,
      subtitle: t('home.hero.subtitle'),
      location: t('home.hero.locations.luxury_hospitality'),
      link: '/projects'
    },
    {
      image: '/images/hero_1_new.webp',
      titleKey: 'home.hero.title2',
      titleFallback: <Trans i18nKey="home.hero.title2">{"Bespoke\n"}<span className="italic font-light">Creations</span></Trans>,
      subtitle: t('home.hero.subtitle'),
      location: t('home.hero.locations.custom_installation'),
      link: '/projects'
    },
  ];

  const exhibitionHighlights: HomeExhibitionItem[] = [
    {
      image: '/Products/Exhibition Artworks/details/highlight design details/1200-1200-55-pins exhibition.webp',
      closeup: '/Products/Exhibition Artworks/details/highlight design details/1200-1200-55-Closeup.webp',
      mockup: '/Products/Exhibition Artworks/details/highlight design details/1200-1200-55-Mockup.webp',
      dimension: '1200 × 1200 mm',
      title: 'Stratified Wave Relief',
      desc: 'High-density sculpted waves capturing dynamic ambient light and soft sculptural shadows.',
      detailUrl: '/catalogue/exhibition-artworks?art=1200-1200-55.webp',
    },
    {
      image: '/Products/Exhibition Artworks/details/highlight design details/875-875-65 (6)-pins exhibition.webp',
      closeup: '/Products/Exhibition Artworks/details/highlight design details/875-875-65 (6)-Closeup.webp',
      mockup: '/Products/Exhibition Artworks/details/highlight design details/875-875-65 (6)-Mockup.webp',
      dimension: '875 × 875 mm',
      title: 'Earth Folds & Mineral Strata',
      desc: 'Nuanced tactile textures blending raw mineral plaster and architectural earth tones.',
      detailUrl: '/catalogue/exhibition-artworks?art=875-875-65%20(6)',
    },
    {
      image: '/Products/Exhibition Artworks/details/highlight design details/1000-1000-60-pins exhibition.webp',
      closeup: '/Products/Exhibition Artworks/details/highlight design details/1000-1000-60-Closeup.webp',
      mockup: '/Products/Exhibition Artworks/details/highlight design details/1000-1000-60-Mockup.webp',
      dimension: '1000 × 1000 mm',
      title: 'Architectural Geometric Relief',
      desc: 'Three-dimensional spatial depth engineered for executive boardrooms and luxury hotel suites.',
      detailUrl: '/catalogue/exhibition-artworks?art=1000-1000-60',
    },
    {
      image: '/Products/Exhibition Artworks/details/highlight design details/1000-1200-100 (2)-pins exhibition.webp',
      closeup: '/Products/Exhibition Artworks/details/highlight design details/1000-1200-100 (2)-Closeup.webp',
      mockup: '/Products/Exhibition Artworks/details/highlight design details/1000-1200-100 (2)-Mockup.webp',
      dimension: '1000 × 1200 mm',
      title: 'Sculptural Plaster Formations',
      desc: 'Deep undulating reliefs with organic hand-worked tactile surfaces.',
      detailUrl: '/catalogue/exhibition-artworks?art=1000-1200-100%20(2)',
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const img = new window.Image();
      img.src = heroSlides[1].image;
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroSlideIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);



  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 800], [1, 0]);
  const imageScale = useTransform(scrollY, [0, 800], [1, 0.95]);

  return (
    <div className="w-full overflow-x-clip">
      <SeoHead
        title={t('seo.home.title', '3D Wall Art Manufacturer for Hotels — Clouds Art, Shenzhen')}
        description={t('seo.home.description', 'Clouds Art is a premier B2B manufacturer of custom 3D wall art, tactile reliefs, and bespoke installations for luxury hotels, interior designers, and architects.')}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Clouds Art",
            "url": "https://www.clouds-art.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.clouds-art.com/en/catalogue?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        ]}
      />

      <section className="relative min-h-screen flex flex-col justify-center items-center pt-24 pb-12 px-4 md:px-8 bg-white overflow-clip">
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative w-full max-w-6xl mx-auto mt-2"
        >
          <div className="absolute top-0 -translate-y-1/2 lg:top-[20%] left-0 lg:-left-20 z-10 pointer-events-none h-20 sm:h-40 md:h-48 w-full">
            <AnimatePresence initial={false}>
              <motion.h1
                key={heroSlideIndex}
                initial={{ x: 0, opacity: 1 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 50, opacity: 0 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="whitespace-pre-line text-[2.5rem] sm:text-6xl md:text-[7rem] lg:text-[8.5rem] font-display font-medium text-spring-text tracking-tighter uppercase leading-[0.85] md:leading-[0.85] absolute w-full text-center lg:text-left lg:w-auto"
              >
                {heroSlides[heroSlideIndex].titleFallback}
              </motion.h1>
            </AnimatePresence>
          </div>

          <motion.div
            style={{ scale: imageScale }}
            className="w-full aspect-[16/9] md:aspect-[21/9] relative z-0 overflow-hidden bg-spring-text/10 group mt-4 md:mt-0"
          >
            <AnimatePresence initial={false}>
              <motion.img
                key={heroSlideIndex}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                src={heroSlides[heroSlideIndex].image}
                alt="Art Space"
                fetchPriority={heroSlideIndex === 0 ? "high" : "auto"}
                decoding={heroSlideIndex === 0 ? "sync" : "async"}
                className="w-full h-full object-cover absolute inset-0"
              />
            </AnimatePresence>
          </motion.div>

          <div className="w-full flex justify-end mt-4 h-4 overflow-visible relative px-4 md:px-0">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={heroSlideIndex + "-text"}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 1.4, ease: [0.25, 1, 0.5, 1] }}
                className="absolute right-4 md:right-0 text-[10px] tracking-widest lowercase font-light flex items-center gap-2 max-w-[80%] text-right"
              >
                <div className="w-1 h-1 rounded-full bg-spring-text/30 animate-pulse" />
                {heroSlides[heroSlideIndex].link ? (
                  <Link
                    to={heroSlides[heroSlideIndex].link as string}
                    className="text-spring-text/80 hover:text-spring-text transition-colors flex items-center gap-1.5 group"
                  >
                    <span className="border-b border-transparent group-hover:border-spring-text/30 transition-colors pb-0.5">{heroSlides[heroSlideIndex].location}</span>
                    <ArrowUpRight size={10} className="opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                  </Link>
                ) : (
                  <span className="text-spring-text/50">{heroSlides[heroSlideIndex].location}</span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 max-w-4xl mx-auto space-y-4 mt-8 md:mt-10 text-center flex flex-col items-center"
        >
          <div className="overflow-visible relative flex justify-center items-center w-full">
            <AnimatePresence initial={false} mode="wait">
              <motion.p
                key={heroSlideIndex}
                initial={{ y: 0, opacity: 1 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                className="text-lg sm:text-xl md:text-2xl text-spring-text font-display italic text-center"
              >
                {heroSlides[heroSlideIndex].subtitle}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2 sm:pt-4 w-full px-4 max-w-xl mx-auto">
            <Link
              to="/catalogue"
              className="w-full sm:w-auto group flex items-center justify-center gap-3 px-8 py-4 bg-spring-accent3 text-black hover:bg-spring-text hover:text-white transition-all duration-300 font-medium tracking-widest text-sm uppercase hover:-translate-y-0.5"
            >
              <span>{t('home.hero.cta.catalogue')}</span>
              <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>

            <Link
              to="/contact"
              className="w-full sm:w-auto group flex items-center justify-center gap-2 px-8 py-4 border border-spring-text text-spring-text hover:bg-spring-text hover:text-white transition-all duration-300 font-medium tracking-widest text-sm uppercase"
            >
              <span>{t('home.hero.cta.quote')}</span>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{ opacity: heroOpacity }}
            className="flex flex-col items-center gap-2 mt-6 sm:mt-8"
          >
            <span className="text-spring-text/70 text-[10px] uppercase tracking-[0.25em]">{t('home.hero.scroll')}</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="w-[1px] h-8 bg-gradient-to-b from-spring-text/40 to-transparent"
            />
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10"
        >
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroSlideIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-[2px] transition-all duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-spring-text ${i === heroSlideIndex ? 'w-8 bg-spring-text' : 'w-3 bg-spring-text/30 hover:bg-spring-text/60'
                }`}
            />
          ))}
        </motion.div>
      </section>

      {/* 2. Trust Indicators Section */}
      <section className="relative w-full bg-spring-sand/70 py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-6 sm:gap-8 md:gap-16 text-[10px] md:text-xs font-medium tracking-[0.2em] uppercase w-full">
            <div className="flex items-center gap-3 transition-opacity hover:opacity-70">
              <FileCheck size={24} strokeWidth={1.5} className="text-spring-accent3 flex-shrink-0" />
              <span className="text-spring-text/80">{t('home.trust.certified')}</span>
            </div>
            <div className="flex items-center gap-3 transition-opacity hover:opacity-70">
              <Clock size={24} strokeWidth={1.5} className="text-spring-accent3 flex-shrink-0" />
              <span className="text-spring-text/80">{t('home.trust.experience')}</span>
            </div>
            <div className="flex items-center gap-3 transition-opacity hover:opacity-70">
              <PackageCheck size={24} strokeWidth={1.5} className="text-spring-accent3 flex-shrink-0" />
              <span className="text-spring-text/80">{t('home.trust.delivered')}</span>
            </div>
            <div className="flex items-center gap-3 transition-opacity hover:opacity-70">
              <Globe size={24} strokeWidth={1.5} className="text-spring-accent3 flex-shrink-0" />
              <span className="text-spring-text/80">{t('home.trust.logistics')}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="relative z-10 bg-spring-bg">

        {/* 3. Exhibition Artworks Showcase — Tactile 3D Reliefs */}
        <section className="py-24 md:py-36 bg-spring-sand/40 border-b border-spring-text/10 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">

            {/* Header with Title & CTA */}
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 md:mb-20 gap-8">
              <div className="max-w-3xl">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="flex items-center gap-4 text-xs font-semibold tracking-widest uppercase text-spring-accent3 mb-4"
                >
                  <span className="w-10 h-[1px] bg-spring-accent3" />
                  {t('home.exhibition_highlights.tag', 'Featured Collection · 3D Tactile Reliefs')}
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="text-3xl sm:text-4xl md:text-6xl font-display font-medium text-spring-text tracking-tight leading-tight"
                >
                  {t('home.exhibition_highlights.title', 'Sculptural Wall Art & Mineral Formations')}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="mt-6 text-base sm:text-lg text-spring-text/70 font-light max-w-2xl leading-relaxed"
                >
                  {t('home.exhibition_highlights.desc', 'Debuted at Maison Shanghai. Handcrafted in high-density sculpted plaster and raw mineral strata, engineered for architectural hospitality and bespoke residential interiors.')}
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex-shrink-0"
              >
                <Link
                  to="/catalogue/exhibition-artworks"
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-spring-text text-white hover:bg-spring-accent3 hover:text-black transition-all duration-300 text-xs uppercase tracking-widest font-medium shadow-sm hover:-translate-y-0.5"
                >
                  <span>{t('home.exhibition_highlights.cta', 'Explore all')}</span>
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>

            {/* Grid of 4 Curated Pieces */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {exhibitionHighlights.map((piece, idx) => (
                <motion.div
                  key={piece.image}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="group flex flex-col bg-white border border-spring-text/10 overflow-hidden hover:shadow-xl hover:border-spring-text/20 transition-all duration-500"
                >
                  <HomeExhibitionMedia piece={piece} isFrench={isFrench} isArabic={isArabic} />

                  <div className="p-6 flex flex-col flex-grow justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-spring-accent3 font-semibold mb-1">
                        {t('home.exhibition_highlights.medium', 'Tactile Mineral Plaster')}
                      </p>
                      <h3 className="font-display text-lg text-spring-text mb-2 tracking-tight">
                        {piece.title}
                      </h3>
                      <p className="text-xs text-spring-text/60 leading-relaxed font-light">
                        {piece.desc}
                      </p>
                    </div>

                    <div className="pt-5 mt-4 border-t border-spring-text/10 flex items-center justify-between gap-2">
                      <Link
                        to={piece.detailUrl}
                        className="text-xs font-semibold uppercase tracking-wider text-spring-text hover:text-spring-accent3 transition-colors inline-flex items-center gap-1 group/btn"
                      >
                        <span>View Piece</span>
                        <ArrowRight size={12} className="group-hover/btn:translate-x-0.5 transition-transform" />
                      </Link>

                      <Link
                        to="/catalogue/exhibition-artworks"
                        className="text-xs font-medium uppercase tracking-wider text-spring-text/60 hover:text-spring-text transition-colors inline-flex items-center gap-1 hover:underline"
                      >
                        <span>Explore All</span>
                        <ArrowUpRight size={12} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bottom Bar Indicator */}
            <div className="mt-14 pt-8 border-t border-spring-text/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-spring-text/60">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-spring-accent3" />
                <span>{t('home.exhibition_highlights.custom_badge', 'All exhibition pieces fully customizable in scale, patina & framing upon commission')}</span>
              </div>
              <div className="flex items-center gap-6">
                <Link
                  to="/exhibitions"
                  className="font-medium text-spring-accent1 hover:text-spring-text tracking-wider uppercase text-[11px] inline-flex items-center gap-1"
                >
                  Maison Shanghai & Exhibitions →
                </Link>
                <Link
                  to="/contact?subject=Inquiry – Bespoke Exhibition Reliefs"
                  className="font-medium text-spring-text hover:text-spring-accent3 underline tracking-wider uppercase text-[11px]"
                >
                  {t('home.exhibition_highlights.quote_cta', 'Request Bespoke Quote')} →
                </Link>
              </div>
            </div>

          </div>
        </section>

        {/* 4. About Us Section */}
        <section className="min-h-screen w-full flex flex-col justify-center py-32 md:py-48 overflow-hidden bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">

            <div className="mb-20 md:mb-32 max-w-4xl">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="flex items-center gap-4 text-xs font-semibold tracking-widest uppercase text-spring-accent3 mb-8"
              >
                <span className="w-12 h-[1px] bg-spring-accent3" />
                {t('home.about.subtitle')}
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-4xl sm:text-5xl md:text-7xl lg:text-[7rem] font-display font-medium leading-[0.95] text-spring-text tracking-tight"
              >
                {t('home.about.title')}
              </motion.h2>
            </div>

            {/* Bottom Split Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 lg:gap-32 items-end relative">

              {/* Left: Text */}
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-4 space-y-8 pb-4"
              >
                <p className="text-lg text-spring-text/70 font-light leading-relaxed">
                  {t('home.about.desc')}
                </p>
                <div className="pt-2">
                  <Link to="/about" className="inline-flex items-center gap-2 font-medium text-spring-text hover:text-spring-accent3 transition-colors border-b border-spring-text/20 hover:border-spring-accent3 pb-1">
                    {t('home.about.cta')} <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>

              {/* Right: Massive Image */}
              <motion.div
                initial={{ opacity: 0, x: 100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="lg:col-span-8 w-full"
              >
                <div className="w-full aspect-[16/9] md:aspect-[21/9] lg:aspect-[16/9] p-3 border border-spring-text/10 bg-white/40 group">
                  <div className="w-full h-full relative overflow-hidden">
                    <img
                      src="/images/pending clouds art team.webp"
                      alt="Our Studio"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-spring-text/0 group-hover:bg-spring-text/5 transition-colors duration-500" />
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* Video & Text Presentation */}
        <section className="py-20 md:py-28 lg:py-36 overflow-hidden bg-spring-sand/70">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">

            {/* Section Label */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-10 md:mb-14"
            >
              <div className="w-8 h-[1px] bg-spring-accent3" />
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-spring-accent3">
                {t('home.videoSection.label')}
              </span>
            </motion.div>

            {/* Grid: Text | Video */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">

              {/* Left: Text — 5 cols */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-5 flex flex-col justify-center space-y-5 order-1"
              >
                <p className="text-spring-text/75 text-sm md:text-base font-light leading-relaxed">
                  {t('home.videoSection.text1')}
                </p>
                <p className="text-spring-text/75 text-sm md:text-base font-light leading-relaxed">
                  {t('home.videoSection.text2')}
                </p>
                <p className="text-spring-text/75 text-sm md:text-base font-light leading-relaxed">
                  {t('home.videoSection.text3')}
                </p>
              </motion.div>

              {/* Right: YouTube Video — 7 cols */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="lg:col-span-7 order-2"
              >
                <div className="relative w-full aspect-video overflow-hidden bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube-nocookie.com/embed/QANOhLC98sY?controls=1"
                    title="Pending Clouds Art Video"
                    loading="lazy"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full"
                  ></iframe>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* 4. Key Capabilities (Zigzag Layout) */}
        <section className="py-32 md:py-48 overflow-hidden bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-12">

            <div className="mb-32 md:mb-40 text-center flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                className="flex items-center gap-4 text-xs font-semibold tracking-widest uppercase text-spring-accent3 mb-8"
              >
                <div className="w-8 h-[1px] bg-spring-accent1" />
                <span className="text-spring-accent1">{t('home.capabilities.expertise')}</span>
                <div className="w-8 h-[1px] bg-spring-accent2" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-medium tracking-tight"
              >
                {t('home.capabilities.title')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ delay: 0.2 }}
                className="text-spring-text/70 font-light text-lg max-w-2xl mt-6 leading-relaxed"
              >
                <Trans i18nKey="home.capabilities.desc">
                  Explore our comprehensive <strong className="font-medium text-spring-text">B2B artwork production services</strong>. From bulk handmade paintings to intricate 3D wall art and custom metal sculptures, our Shenzhen manufacturing facility delivers premium <strong className="font-medium text-spring-text">commercial interior decor</strong> engineered for luxury hotels, corporate offices, and high-end retail spaces.
                </Trans>
              </motion.p>
            </div>

            <div className="space-y-32 md:space-y-48 lg:space-y-64">
              {homeCategories.map((category, i) => {
                const isEven = i % 2 === 0;
                const desc = t(`data.categories.${category.slug}.desc`);
                const displayImage = category.image;

                return (
                  <div key={category.slug} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-24 lg:gap-40 group cursor-pointer`}>

                    {/* Image */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? -100 : 100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8 }}
                      className="w-full md:w-1/2 aspect-[4/3] bg-spring-text/5 relative overflow-hidden"
                    >
                      <Link to={`/catalogue/${category.slug}`}>
                        <img
                          src={displayImage}
                          alt={category.name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-contain p-6 transition-transform duration-1000 group-hover:scale-105 mix-blend-multiply"
                        />
                        <div className="absolute inset-0 bg-spring-text/0 group-hover:bg-spring-text/5 transition-colors duration-500" />
                        <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm text-spring-text flex items-center justify-center opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 z-20">
                          <ArrowRight size={18} strokeWidth={1.5} />
                        </div>
                      </Link>
                    </motion.div>

                    {/* Text */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? 100 : -100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="w-full md:w-1/2"
                    >
                      <Link to={`/catalogue/${category.slug}`} className="block">
                        <h3 className={`text-3xl sm:text-4xl lg:text-5xl font-display font-medium mb-4 sm:mb-6 transition-colors ${isEven ? 'group-hover:text-spring-accent1' : 'group-hover:text-spring-accent2'}`}>{t(`data.categories.${category.slug}.name`)}</h3>
                        <p className="text-spring-text/60 font-light text-base sm:text-lg mb-8 leading-relaxed">{desc}</p>
                        <div className={`w-16 h-[1px] bg-spring-text/20 group-hover:w-24 transition-all duration-500 ${isEven ? 'group-hover:bg-spring-accent1' : 'group-hover:bg-spring-accent2'}`} />
                      </Link>
                    </motion.div>

                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* 5. OEM vs ODM Highlight */}
        <section className="py-32 md:py-48 px-4 bg-spring-sand/70">
          <div className="max-w-7xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-24 md:mb-32 flex flex-col items-center">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-medium tracking-tight">{t('home.oem_odm.title')}</h2>
              <p className="text-spring-text/70 font-light text-lg max-w-3xl mt-6 leading-relaxed">
                <Trans i18nKey="home.oem_odm.desc">
                  We offer end-to-end <strong className="font-medium text-spring-text">OEM and ODM artwork manufacturing</strong>. Whether you need exclusive private label art production or original concept development, our global logistics and strict quality control ensure your <strong className="font-medium text-spring-text">commercial art projects</strong> are delivered flawlessly, on time, and within budget.
                </Trans>
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-32">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="p-6 sm:p-8 md:p-12 border border-spring-text/10"
              >
                <h3 className="text-2xl font-medium mb-4">{t('home.oem_odm.oem.title')}</h3>
                <p className="text-spring-text/70 font-light mb-8 leading-relaxed">
                  {t('home.oem_odm.oem.desc')}
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3"><ShieldCheck size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.oem.bullet1')}</li>
                  <li className="flex items-center gap-3"><TrendingUp size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.oem.bullet2')}</li>
                  <li className="flex items-center gap-3"><Crosshair size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.oem.bullet3')}</li>
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="p-6 sm:p-8 md:p-12 border border-spring-text/10 group relative overflow-hidden"
              >
                <h3 className="text-2xl font-medium mb-4 relative z-10">{t('home.oem_odm.odm.title')}</h3>
                <p className="text-spring-text/70 font-light mb-8 leading-relaxed relative z-10">
                  {t('home.oem_odm.odm.desc')}
                </p>
                <ul className="space-y-3 mb-8 relative z-10">
                  <li className="flex items-center gap-3"><Lightbulb size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.odm.bullet1')}</li>
                  <li className="flex items-center gap-3"><Lock size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.odm.bullet2')}</li>
                  <li className="flex items-center gap-3"><Rocket size={16} strokeWidth={1.5} className="text-spring-accent3 shrink-0" /> {t('home.oem_odm.odm.bullet3')}</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 6. Featured Projects */}
        <section className="py-32 md:py-48 bg-white text-spring-text">
          <div className="max-w-7xl mx-auto px-4 md:px-12">

            <div className="flex flex-col md:flex-row justify-between items-end mb-32 md:mb-40 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="max-w-2xl"
              >
                <div className="flex items-center gap-4 text-xs font-semibold tracking-widest uppercase mb-8">
                  <div className="w-12 h-[1px] bg-spring-accent1" />
                  <span className="text-spring-accent1">{t('home.portfolio.subtitle')}</span><span className="text-spring-accent2 -ml-3">{t('home.portfolio.subtitle2')}</span>
                </div>
                <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-medium tracking-tight mb-4 sm:mb-6">{t('home.portfolio.title')}</h2>
                <p className="text-spring-text/70 font-light text-lg leading-relaxed">
                  {t('home.portfolio.desc')}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Link to="/projects" className="shrink-0 inline-flex items-center gap-2 font-medium text-spring-text hover:text-spring-accent3 transition-colors border-b border-spring-text/20 hover:border-spring-accent3 pb-1">
                  {t('home.portfolio.cta')} <ArrowRight size={16} />
                </Link>
              </motion.div>
            </div>

            <div className="space-y-32 md:space-y-48">
              {projects.slice(0, 3).map((proj, i) => {
                const isEven = i % 2 === 0;
                return (
                  <Link to={`/projects/${proj.id}`} key={proj.id} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12 lg:gap-24 group cursor-pointer`}>

                    {/* Image */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? -100 : 100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8 }}
                      className="w-full md:w-[60%] aspect-[4/3] md:aspect-[16/10] bg-spring-text/5 relative overflow-hidden"
                    >
                      <img
                        src={proj.images[0]}
                        alt={proj.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-spring-dark/0 group-hover:bg-spring-dark/10 transition-colors duration-500" />

                      <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 z-20">
                        <ArrowUpRight size={18} strokeWidth={1.5} className="text-spring-text" />
                      </div>
                    </motion.div>

                    {/* Text */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? 100 : -100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: false, amount: 0.3 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="w-full md:w-[40%] space-y-6"
                    >
                      <div className={`flex items-center gap-4 text-xs font-semibold tracking-widest uppercase ${isEven ? 'text-spring-accent1' : 'text-spring-accent2'}`}>
                        <div className={`w-8 h-[1px] ${isEven ? 'bg-spring-accent1' : 'bg-spring-accent2'}`} />
                        {t(`data.projects.${proj.id}.sector`, proj.sector)}
                      </div>
                      <h3 className={`text-4xl lg:text-5xl font-display font-medium transition-colors ${isEven ? 'group-hover:text-spring-accent1' : 'group-hover:text-spring-accent2'}`}>{t(`data.projects.${proj.id}.title`, proj.title)}</h3>
                      <p className="text-spring-text/60 font-light text-lg pb-8 border-b border-spring-text/10">{t(`data.projects.${proj.id}.desc`, proj.desc)}</p>

                      <div className="pt-2 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                        <span className={`inline-flex items-center gap-2 font-medium pb-1 border-b ${isEven ? 'text-spring-accent1 border-spring-accent1' : 'text-spring-accent2 border-spring-accent2'}`}>
                          {t('home.portfolio.view_project')} <ArrowRight size={16} />
                        </span>
                      </div>
                    </motion.div>

                  </Link>
                )
              })}
            </div>
          </div>
        </section>

        {/* 6.4 Clients */}
        <section className="py-32 md:py-40 bg-spring-sand/70 text-spring-text overflow-hidden flex flex-col justify-center">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] text-spring-text/80 uppercase">{t('home.clients.title')}</p>
          </div>
          <div className="w-full inline-flex flex-nowrap">
            <motion.div
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="flex whitespace-nowrap gap-16 px-8 items-center w-max"
            >
              {[
                { name: "Marriott", logo: "/clients/marriott.svg" },
                { name: "The Ritz-Carlton", logo: "/clients/ritz-carlton.svg" },
                { name: "Sheraton", logo: "/clients/sheraton.svg" },
                { name: "IHG", logo: "/clients/ihg.svg" },
                { name: "Hyatt", logo: "/clients/hyatt.svg" },
                { name: "Mandarin Oriental", logo: "/clients/mandarin-oriental.svg" },
                { name: "Shangri-La", logo: "/clients/shangri-la.svg" },
                { name: "Four Seasons", logo: "/clients/four-seasons.svg" },
                { name: "Fairmont", logo: "/clients/fairmont.svg" },
                { name: "Hilton", logo: "/clients/hilton.svg" },
                { name: "Marriott", logo: "/clients/marriott.svg" },
                { name: "The Ritz-Carlton", logo: "/clients/ritz-carlton.svg" },
                { name: "Sheraton", logo: "/clients/sheraton.svg" },
                { name: "IHG", logo: "/clients/ihg.svg" },
                { name: "Hyatt", logo: "/clients/hyatt.svg" },
                { name: "Mandarin Oriental", logo: "/clients/mandarin-oriental.svg" },
                { name: "Shangri-La", logo: "/clients/shangri-la.svg" },
                { name: "Four Seasons", logo: "/clients/four-seasons.svg" },
                { name: "Fairmont", logo: "/clients/fairmont.svg" },
                { name: "Hilton", logo: "/clients/hilton.svg" }
              ].map((client, i) => (
                <img
                  key={i}
                  src={client.logo}
                  alt={client.name}
                  loading="lazy"
                  decoding="async"
                  className="w-32 md:w-40 h-16 md:h-20 object-contain grayscale brightness-0 opacity-40 hover:opacity-100 transition-opacity duration-300"
                />
              ))}
            </motion.div>
          </div>
        </section>

        {/* 6.5 Testimonials */}
        <section className="py-32 md:py-48 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-medium tracking-tight">{t('home.testimonials.title')}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
              {[
                { id: 't1' },
                { id: 't2' },
                { id: 't3' }
              ].map((tItem, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2, duration: 0.6 }}
                  className="p-6 sm:p-10 bg-white border border-spring-text/5 flex flex-col justify-between relative group transition-all duration-500"
                >
                  <div>
                    <div className="flex items-center gap-1 mb-6 text-spring-accent3 relative z-10">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={16} fill="currentColor" strokeWidth={0} />
                      ))}
                    </div>
                    <p className="text-spring-text/80 font-light italic leading-relaxed mb-8 relative z-10">"{t(`home.testimonials.items.${tItem.id}.text`)}"</p>
                  </div>
                  <div className="relative z-10 pt-6 border-t border-spring-text/10">
                    <p className="text-base font-medium text-spring-text tracking-wide">{t(`home.testimonials.items.${tItem.id}.author`)}</p>
                    <p className="text-[10px] font-medium text-spring-text/50 uppercase tracking-widest mt-2">{t(`home.testimonials.items.${tItem.id}.role`)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 6.5. FAQ Section */}
        <section className="py-32 md:py-48 px-4 md:px-12 bg-spring-sand/70">
          <div className="max-w-5xl mx-auto">
            <motion.div {...fadeIn} className="mb-24 md:mb-32">
              <h2 className="text-4xl md:text-6xl font-light text-spring-text tracking-tight mb-4">
                {t('home.faq.title')}
              </h2>
              <p className="text-spring-text/70 text-lg font-light max-w-2xl">
                {t('home.faq.desc')}
              </p>
            </motion.div>
            <div className="mb-12 border-t border-spring-text/10">
              <HomeFAQItem
                question={t('home.faq.q1.q')}
                answer={t('home.faq.q1.a')}
              />
              <HomeFAQItem
                question={t('home.faq.q2.q')}
                answer={t('home.faq.q2.a')}
              />
              <HomeFAQItem
                question={t('home.faq.q3.q')}
                answer={t('home.faq.q3.a')}
              />
            </div>
            <div className="text-center md:text-left">
              <Link to="/faq" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-spring-text hover:text-spring-accent3 transition-colors">
                {t('home.faq.cta')} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* 7. Call to Action */}
        <section className="pt-32 pb-16 md:pt-48 md:pb-24 px-4 w-full bg-white text-center">
          <motion.div {...fadeIn} className="max-w-5xl mx-auto">
            <h2 className="text-5xl sm:text-6xl md:text-[7rem] font-display font-medium tracking-tighter leading-[0.9] mb-8 sm:mb-10">
              {t('home.cta.title_pt1')} <br className="hidden md:block" />
              <span className="font-semibold italic">{t('home.cta.title_pt2')}</span>
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="px-8 py-4 bg-spring-text text-spring-bg hover:bg-spring-text/90 transition-colors">
                {t('home.cta.btn1')}
              </Link>
              <Link to="/services" className="px-8 py-4 border border-spring-text/20 hover:bg-spring-text/5 transition-colors">
                {t('home.cta.btn2')}
              </Link>
            </div>
          </motion.div>
        </section>
      </div> {/* Fin du wrapper bg-spring-bg */}
    </div>
  );
}
